import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Loader2, X } from "lucide-react";

export default function AdminReports() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [rejectingId, setRejectingId] = useState(null);
  const [reason, setReason] = useState("");
  const [uploadingId, setUploadingId] = useState(null);

  const load = () => api.get("/admin/report-requests").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const upload = async (id, file) => {
    setUploadingId(id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/report-requests/${id}/file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("common.saved"));
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploadingId(null); }
  };

  const reject = async () => {
    await api.patch(`/admin/report-requests/${rejectingId}/status`, { status: "rejected", reject_reason: reason });
    setRejectingId(null); setReason(""); load();
  };

  return (
    <div className="p-6 md:p-10" data-testid="admin-reports-page">
      <h1 className="font-serif-cn text-3xl text-[#003645] font-semibold">{t("adminP3.reports")}</h1>

      <div className="mt-6 overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-reports-table">
          <thead><tr className="text-left text-xs uppercase text-[#4a6e77]">
            <th className="py-3 px-4 border-b border-[#e5ebeb]">User</th>
            <th className="py-3 px-4 border-b border-[#e5ebeb]">Type</th>
            <th className="py-3 px-4 border-b border-[#e5ebeb]">Birth</th>
            <th className="py-3 px-4 border-b border-[#e5ebeb]">Status</th>
            <th className="py-3 px-4 border-b border-[#e5ebeb]"></th>
          </tr></thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id} data-testid={`admin-report-${r.id}`}>
                <td className="py-3 px-4 border-b border-[#e5ebeb]"><div className="text-[#003645] font-medium">{r.user_name}</div><div className="text-xs text-[#4a6e77]">{r.user_email}</div></td>
                <td className="py-3 px-4 border-b border-[#e5ebeb] text-[#4a6e77]">{t(`reports.types.${r.report_type}`)}</td>
                <td className="py-3 px-4 border-b border-[#e5ebeb] text-xs text-[#4a6e77]">
                  {r.birth_snapshot?.gender} · {r.birth_snapshot?.birth_year}-{r.birth_snapshot?.birth_month}-{r.birth_snapshot?.birth_day} {r.birth_snapshot?.birth_hour}:00
                </td>
                <td className="py-3 px-4 border-b border-[#e5ebeb]">
                  <span className={`text-xs px-2 py-1 rounded-full ${r.status === "completed" ? "bg-[#00868e]/10 text-[#00868e]" : r.status === "rejected" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>
                    {t(`reports.statuses.${r.status}`)}
                  </span>
                  {r.reject_reason && <div className="text-xs text-red-700 mt-1">{r.reject_reason}</div>}
                </td>
                <td className="py-3 px-4 border-b border-[#e5ebeb]">
                  <label className="inline-flex items-center gap-1 text-[#005870] hover:text-[#00868e] text-xs cursor-pointer" data-testid={`upload-report-${r.id}`}>
                    {uploadingId === r.id ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} {t("adminP3.uploadReport")}
                    <input type="file" accept="application/pdf" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(r.id, f); e.target.value = ""; }} />
                  </label>
                  {r.status !== "rejected" && (
                    <button onClick={() => { setRejectingId(r.id); setReason(""); }} className="ml-3 text-red-700 text-xs" data-testid={`reject-report-${r.id}`}>Reject</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {rejectingId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between mb-3"><div className="text-sm text-[#4a6e77]">Reason</div><button onClick={() => setRejectingId(null)}><X size={14} /></button></div>
            <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} className="w-full px-3 py-2 border border-[#e5ebeb] rounded-md" data-testid="report-reject-reason" />
            <div className="mt-4 flex gap-2">
              <button onClick={reject} className="flex-1 px-4 py-2 rounded-full bg-red-600 text-white" data-testid="report-reject-confirm">{t("common.confirm")}</button>
              <button onClick={() => setRejectingId(null)} className="flex-1 px-4 py-2 rounded-full border border-[#e5ebeb]">{t("common.cancel")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
